import { ReactNode, useMemo, useState } from 'react';
import cn from 'classnames';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  CartesianGrid,
  ComposedChart,
  Area,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { Neutral300, Neutral500, Neutral600 } from '../../../../utils/colors';
import { DivergenceRange } from '../divergence-ranges';
import { WeekRow } from '../data-qa-types';
import { DivergenceLabelsLayer, DivergenceRegionsLayer } from './DivergenceMarkers';
import { MarginalCaretsLayer } from './MarginalCarets';

export type SeriesAxis = 'left' | 'right';

export interface SeriesConfig {
  dataKey: string;
  label: string;
  color: string;
  axis?: SeriesAxis;
  format?: 'currency' | 'number' | 'percent' | 'raw';
  // Dashed, no-fill line — used for the BPM canonical overlay.
  dashed?: boolean;
}

interface DataQaChartProps {
  weeks: WeekRow[];
  series: SeriesConfig[];
  // Contiguous week ranges where this metric's MMM value diverges unusually from its
  // canonical (BPM) value (see divergence-ranges.ts) -- shaded distinctly from the
  // reviewed-range treatment above, with its own marker/label layer (DivergenceMarkers)
  // below.
  divergentRanges?: DivergenceRange[];
  bookmarkWeekIso?: string;
  isColdStart?: boolean;
  height?: number;
  // Heading shown above the chart (e.g. 'Total Spend Over Time').
  title?: string;
  // Hide the series toggle legend (used for single-series charts).
  hideLegend?: boolean;
  // Extra tooltip content for the hovered week, appended below the series rows --
  // e.g. Context Variables' combined outliers chart uses this to list which specific
  // outlier flag(s) fired that week, since a single aggregate series can't say that on
  // its own (see ContextVarsSection). Receives the raw week object backing the hovered
  // point (recharts' Tooltip payload always carries the full source datum, not just the
  // active series' values).
  renderTooltipExtra?: (week: WeekRow) => ReactNode;
}

const tickStyle = { fontSize: 11, fill: Neutral600 };

// Shared by the actual chart Area's strokeDasharray below and the legend swatch's own
// <line> -- a single source of truth so the legend can't visually drift from what the
// chart itself draws (a CSS border-style: dashed swatch read as barely different from
// solid at a glance, per Sean, 2026-09-02 -- rendering the identical SVG dash pattern
// at legend scale is what actually reads as "dashed" the way the chart line does).
const DASHED_STROKE = '6 4';

// Shared by the legend and the hover tooltip -- both need to show which series is MMM
// (solid) vs. BPM (dashed) at a glance, so this is the one place drawing that line
// rather than two near-duplicate <svg> blocks that could drift apart.
const SeriesSwatch = ({
  className,
  color,
  dashed
}: {
  className: string;
  color: string;
  dashed?: boolean;
}) => (
  <svg className={className} width={24} height={10} aria-hidden>
    <line
      x1='0'
      y1='5'
      x2='24'
      y2='5'
      stroke={color}
      strokeWidth={3}
      strokeDasharray={dashed ? DASHED_STROKE : undefined}
      strokeLinecap='round'
    />
  </svg>
);

// Compact axis ticks (e.g. "$1.2M", "160M") — full values would clip the narrow
// y-axis; the tooltip still shows the exact figure.
const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1
});
const formatAxisTick = (value: number, fmt: SeriesConfig['format']) =>
  fmt === 'currency' ? `$${compactFormatter.format(value)}` : compactFormatter.format(value);

const formatValue = (value: number, fmt: SeriesConfig['format']) => {
  if (fmt === 'currency') {
    return formatMoney(value);
  }
  if (fmt === 'percent') {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (fmt === 'raw') {
    return value.toFixed(2);
  }
  return formatNumber(value);
};

const CustomTooltip = ({
  active,
  payload,
  label,
  series,
  renderTooltipExtra
}: {
  active?: boolean;
  payload?: { dataKey: string; value: number; payload: WeekRow }[];
  label?: string;
  series: SeriesConfig[];
  renderTooltipExtra?: (week: WeekRow) => ReactNode;
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const week = payload[0]?.payload;
  return (
    <div className='data-qa-chart__tooltip'>
      <div className='data-qa-chart__tooltip-header'>{label}</div>
      {payload.map(p => {
        const cfg = series.find(s => s.dataKey === p.dataKey);
        if (!cfg) return undefined;
        return (
          <div key={p.dataKey} className='data-qa-chart__tooltip-row'>
            <SeriesSwatch
              className='data-qa-chart__tooltip-swatch'
              color={cfg.color}
              dashed={cfg.dashed}
            />
            <span className='data-qa-chart__tooltip-label'>{cfg.label}</span>
            <span className='data-qa-chart__tooltip-value'>{formatValue(p.value, cfg.format)}</span>
          </div>
        );
      })}
      {week && renderTooltipExtra?.(week)}
    </div>
  );
};

export const DataQaChart = ({
  weeks,
  series,
  divergentRanges = [],
  bookmarkWeekIso,
  isColdStart = false,
  height = 320,
  title,
  hideLegend = false,
  renderTooltipExtra
}: DataQaChartProps) => {
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const toggleSeries = (dataKey: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
  };

  const reviewedRange = useMemo(() => {
    if (isColdStart || !bookmarkWeekIso) return undefined;
    const firstWeek = weeks[0]?.weekIso;
    if (!firstWeek) return undefined;
    // The XAxis below is keyed on weekLabel (a differently-formatted display string,
    // e.g. "Jan 2"), not weekIso (e.g. "2023-01-02") -- Recharts resolves a categorical
    // x1/x2/x by matching the axis domain's category values, so passing weekIso
    // straight through here never matches and this "already reviewed up to here"
    // shading/line silently fails to render (or renders at the wrong position). Look
    // both boundaries up by weekIso, same as divergentRanges' own xValue already does.
    const weekLabelByIso = new Map(weeks.map(w => [w.weekIso, w.weekLabel]));
    const from = weekLabelByIso.get(firstWeek);
    const to = weekLabelByIso.get(bookmarkWeekIso);
    if (!from || !to) return undefined;
    return { from, to };
  }, [bookmarkWeekIso, isColdStart, weeks]);

  const hasDualAxis = series.some(s => s.axis === 'right');

  return (
    <div className='data-qa-chart'>
      {title && <div className='data-qa-chart__title'>{title}</div>}
      <div className='data-qa-chart__body'>
        <div className='data-qa-chart__plot' style={{ height }}>
          <AutoSizer
            renderProp={({ width, height: h }) => (
              <ComposedChart
                width={width ?? 0}
                height={h ?? 0}
                data={weeks}
                margin={{ top: 24, right: 32, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke={Neutral300} strokeWidth={1} vertical={false} />
                <XAxis
                  dataKey='weekLabel'
                  tick={tickStyle}
                  axisLine={false}
                  tickLine={false}
                  interval={Math.max(0, Math.floor(weeks.length / 12))}
                />
                <YAxis
                  yAxisId='left'
                  tick={tickStyle}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => formatAxisTick(Number(v), series[0]?.format)}
                  width={56}
                />
                {hasDualAxis && (
                  <YAxis
                    yAxisId='right'
                    orientation='right'
                    tick={tickStyle}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={v => formatAxisTick(Number(v), 'number')}
                    width={56}
                  />
                )}
                <Tooltip
                  isAnimationActive={false}
                  content={
                    <CustomTooltip series={series} renderTooltipExtra={renderTooltipExtra} />
                  }
                />
                {reviewedRange && (
                  <>
                    <ReferenceArea
                      yAxisId='left'
                      x1={reviewedRange.from}
                      x2={reviewedRange.to}
                      fill={Neutral500}
                      fillOpacity={0.08}
                    />
                    <ReferenceLine
                      yAxisId='left'
                      x={reviewedRange.to}
                      stroke={Neutral600}
                      strokeDasharray='4 4'
                      label={{
                        value: 'Prior approval',
                        position: 'insideTopRight',
                        fill: Neutral600,
                        fontSize: 10
                      }}
                    />
                  </>
                )}
                {divergentRanges.length > 0 && <DivergenceRegionsLayer ranges={divergentRanges} />}
                {series.map(s => (
                  <Area
                    key={s.dataKey}
                    type='monotone'
                    dataKey={s.dataKey}
                    stroke={hiddenSeries.has(s.dataKey) ? 'transparent' : s.color}
                    strokeDasharray={s.dashed ? DASHED_STROKE : undefined}
                    fill={s.color}
                    fillOpacity={hiddenSeries.has(s.dataKey) || s.dashed ? 0 : 0.15}
                    strokeWidth={2}
                    dot={false}
                    yAxisId={s.axis === 'right' ? 'right' : 'left'}
                    isAnimationActive={false}
                  />
                ))}
                {divergentRanges.length > 0 && <DivergenceLabelsLayer ranges={divergentRanges} />}
                <MarginalCaretsLayer weeks={weeks} />
              </ComposedChart>
            )}
          />
        </div>
        {!hideLegend && (
          <div className='data-qa-chart__legend'>
            {series.map(s => (
              <InteractiveSpan
                key={s.dataKey}
                className={cn('data-qa-chart__legend-item', {
                  'data-qa-chart__legend-item--hidden': hiddenSeries.has(s.dataKey)
                })}
                onClick={() => toggleSeries(s.dataKey)}
              >
                <SeriesSwatch
                  className='data-qa-chart__legend-swatch'
                  color={s.color}
                  dashed={s.dashed}
                />
                <span className='data-qa-chart__legend-label'>{s.label}</span>
              </InteractiveSpan>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
